import { z } from "zod";

/**
 * Auth schemas — mirror del Pydantic `dpanel_schemas/auth/__init__.py`.
 * Focus Phase 8H: MFA enroll/verify, recovery codes, sessions list,
 * password change. Phase 10A: local login (email+password Argon2id).
 */

/* ────────────────────────────────────────────── Local login */

export const LoginRequestSchema = z
  .object({
    email: z.string().email("validation.email"),
    password: z
      .string()
      .min(8, "validation.password_login_min")
      .max(512, "validation.password_max"),
  })
  .strict();

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const LoginResponseSchema = z
  .object({
    user_id: z.guid(),
    session_id: z.guid(),
    role: z.string(),
    requires_mfa: z.boolean(),
    requires_password_change: z.boolean().default(false),
    mfa_challenge_id: z.guid().nullable().optional(),
  })
  .passthrough();

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/* ────────────────────────────────────────────── MFA enroll */

export const MFA_ALGORITHMS = ["SHA1", "SHA256", "SHA512"] as const;
export type MfaAlgorithm = (typeof MFA_ALGORITHMS)[number];

export const MFA_DIGITS = [6, 7, 8] as const;
export type MfaDigits = (typeof MFA_DIGITS)[number];

export const MfaEnrollRequestSchema = z
  .object({
    algorithm: z.enum(MFA_ALGORITHMS).default("SHA1"),
    digits: z
      .union([z.literal(6), z.literal(7), z.literal(8)])
      .default(6),
    period_seconds: z.number().int().min(15).max(120).default(30),
  })
  .strict();

export type MfaEnrollRequest = z.infer<typeof MfaEnrollRequestSchema>;

export const MfaEnrollResponseSchema = z
  .object({
    otpauth_uri: z.string(),
    secret_b32: z.string(),
    qr_provisioning_url: z.string().nullable().optional(),
  })
  .strict();

export type MfaEnrollResponse = z.infer<typeof MfaEnrollResponseSchema>;

/* ────────────────────────────────────────────── MFA verify */

export const MfaVerifyRequestSchema = z
  .object({
    code: z
      .string()
      .regex(/^\d{6,8}$/, "validation.mfa_code"),
  })
  .strict();

export type MfaVerifyRequest = z.infer<typeof MfaVerifyRequestSchema>;

export const MfaVerifyResponseSchema = z
  .object({
    session_id: z.guid(),
    mfa_assured_until: z.string(),
  })
  .strict();

export type MfaVerifyResponse = z.infer<typeof MfaVerifyResponseSchema>;

/* ────────────────────────────────────────────── Step-up (POST /auth/step-up) */

export const STEP_UP_ACTION_CLASSES = ["destructive", "admin", "bypass"] as const;
export type StepUpActionClass = (typeof STEP_UP_ACTION_CLASSES)[number];

export const StepUpRequestSchema = z
  .object({
    action_class: z.enum(STEP_UP_ACTION_CLASSES),
    mfa_code: z.string().regex(/^\d{6,8}$/, "validation.mfa_code"),
  })
  .strict();

export type StepUpRequest = z.infer<typeof StepUpRequestSchema>;

export const StepUpResponseSchema = z
  .object({
    step_up_token: z.string(),
    expires_at: z.string(),
    action_class: z.string(),
  })
  .passthrough();

export type StepUpResponse = z.infer<typeof StepUpResponseSchema>;

/* ────────────────────────────────────────────── Recovery codes */

export const RecoveryCodesGenerateResponseSchema = z
  .object({
    codes: z.array(z.string()).length(10),
    generated_at: z.string(),
  })
  .strict();

export type RecoveryCodesGenerateResponse = z.infer<
  typeof RecoveryCodesGenerateResponseSchema
>;

/* ────────────────────────────────────────────── Sessions */

export const SessionListItemSchema = z
  .object({
    session_id: z.guid(),
    user_id: z.guid(),
    role: z.string(),
    tenant_id: z.guid().nullable().optional(),
    created_at: z.string(),
    last_seen_at: z.string(),
    ip: z.string().nullable().optional(),
    user_agent_hash: z.string().nullable().optional(),
    mfa_assured_until: z.string().nullable().optional(),
    is_current: z.boolean().default(false),
  })
  .passthrough();

export type SessionListItem = z.infer<typeof SessionListItemSchema>;

/* ────────────────────────────────────────────── Password change */

/**
 * Composition checks (preventive UX filter on the client).
 *
 * Trade-off: NIST 800-63B (citato da CLAUDE.md §8) sconsiglia esplicitamente
 * le composition rules — riconducono a "Password1!" invece di passphrase
 * lunghe. Le applichiamo comunque qui come filtro preventivo richiesto dal
 * prodotto + il backend `PasswordPolicy` (heuristica entropy + breach
 * substring) resta source of truth con regole piu' robuste.
 */
export const PASSWORD_HAS_UPPER = /[A-Z]/;
export const PASSWORD_HAS_LOWER = /[a-z]/;
export const PASSWORD_HAS_DIGIT = /[0-9]/;
export const PASSWORD_HAS_SYMBOL = /[^A-Za-z0-9]/;

export const PasswordChangeRequestSchema = z
  .object({
    current_password: z
      .string()
      .min(8, "validation.password_login_min")
      .max(512),
    new_password: z
      .string()
      .min(12, "validation.password_min")
      .max(128, "validation.password_max")
      .refine((p) => PASSWORD_HAS_UPPER.test(p), {
        message: "validation.password_need_upper",
      })
      .refine((p) => PASSWORD_HAS_LOWER.test(p), {
        message: "validation.password_need_lower",
      })
      .refine((p) => PASSWORD_HAS_DIGIT.test(p), {
        message: "validation.password_need_digit",
      })
      .refine((p) => PASSWORD_HAS_SYMBOL.test(p), {
        message: "validation.password_need_symbol",
      }),
    confirm_password: z.string().min(12).max(128),
  })
  .strict()
  .refine((v) => v.new_password === v.confirm_password, {
    message: "validation.password_mismatch",
    path: ["confirm_password"],
  })
  .refine((v) => v.new_password !== v.current_password, {
    message: "validation.password_same_as_current",
    path: ["new_password"],
  });

export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequestSchema>;

/* ────────────────────────────────────────────── Activation (Phase 10G) */

/**
 * Mirror del Pydantic ActivationStatusResponse: GET /auth/activation/{token}
 * — pre-form check sulla validity del token. NON consuma il token.
 */
export const ActivationStatusResponseSchema = z
  .object({
    valid: z.boolean(),
    expires_at: z.string().nullable().optional(),
    kind: z.enum(["primary", "intermediate"]).nullable().optional(),
    requires_password: z.boolean().default(true),
  })
  .passthrough();

export type ActivationStatusResponse = z.infer<typeof ActivationStatusResponseSchema>;

/**
 * Mirror del Pydantic ActivationCompleteRequest: POST /auth/activation/{token}
 * — set password + opt enroll MFA. Consuma il token (single-use).
 */
export const ActivationCompleteRequestSchema = z
  .object({
    new_password: z
      .string()
      .min(12, "validation.password_min")
      .max(128, "validation.password_max"),
    enroll_mfa: z.boolean().default(true),
  })
  .strict();

export type ActivationCompleteRequest = z.infer<typeof ActivationCompleteRequestSchema>;

/* ────────────────────────────────────────────── Password reset (forgot flow) */

/** POST /auth/password/forgot — sempre 202 (anti-enumeration). */
export const PasswordForgotRequestSchema = z
  .object({ email: z.string().email("validation.email") })
  .strict();

export type PasswordForgotRequest = z.infer<typeof PasswordForgotRequestSchema>;

/** POST /auth/password/reset — token (dall'URL) + nuova password + OTP opzionale
 * (richiesto lato server solo se l'account ha la TOTP enrollata). */
export const PasswordResetRequestSchema = z
  .object({
    token: z.string().min(16).max(512),
    new_password: z.string().min(12, "validation.password_min").max(128, "validation.password_max"),
    otp_code: z
      .string()
      .regex(/^\d{6,8}$/, "validation.mfa_code")
      .optional(),
  })
  .strict();

export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;
